import { useState, useEffect, useRef } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "../ui/dialog";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Label } from "../ui/label";
import { Badge } from "../ui/badge";
import { Select } from "../ui/select";
import { Loader2, PhoneCall, Search, ArrowRight, CheckCircle2, AlertTriangle } from "lucide-react";
import { toast } from "sonner";
import {
    startTestCall, getCampaignCallById, fetchRetellAgents, fetchRetellPhoneNumbers
} from "../../services/crmServices/campaignService";
import { fetchAvailableLeads } from "../../services/crmServices/leadService";

const CALL_STATUS_STYLES = {
    queued: "bg-gray-100 text-gray-600",
    calling: "bg-green-100 text-green-700",
    completed: "bg-blue-100 text-blue-700",
    failed: "bg-red-100 text-red-700",
    cancelled: "bg-gray-100 text-gray-400"
};

export function TestCallModal({ open, onClose }) {
    // Setup state
    const [agents, setAgents] = useState([]);
    const [phoneNumbers, setPhoneNumbers] = useState([]);
    const [retellLoading, setRetellLoading] = useState(false);
    const [retellError, setRetellError] = useState("");
    const [agentId, setAgentId] = useState("");
    const [fromNumber, setFromNumber] = useState("");

    // Lead search
    const [leadSearch, setLeadSearch] = useState("");
    const [leadResults, setLeadResults] = useState([]);
    const [leadSearching, setLeadSearching] = useState(false);
    const [selectedLead, setSelectedLead] = useState(null);

    // Call state
    const [starting, setStarting] = useState(false);
    const [activeCallId, setActiveCallId] = useState(null);
    const [call, setCall] = useState(null);
    const pollTimer = useRef(null);

    // Load Retell agents/numbers when opened
    useEffect(() => {
        if (!open || agents.length > 0) return;
        (async () => {
            setRetellLoading(true);
            setRetellError("");
            try {
                const [agentsRes, numbersRes] = await Promise.all([
                    fetchRetellAgents(), fetchRetellPhoneNumbers()
                ]);
                setAgents(Array.isArray(agentsRes?.data) ? agentsRes.data : []);
                setPhoneNumbers(Array.isArray(numbersRes?.data) ? numbersRes.data : []);
            } catch (err) {
                setRetellError(err?.response?.data?.message || "Failed to load Retell agents. Check RetellAI settings.");
            } finally {
                setRetellLoading(false);
            }
        })();
    }, [open, agents.length]);

    // Debounced lead search
    useEffect(() => {
        if (!open || !leadSearch.trim() || leadSearch.trim().length < 2) {
            setLeadResults([]);
            return;
        }
        setLeadSearching(true);
        const timer = setTimeout(async () => {
            try {
                const res = await fetchAvailableLeads(1, 10, { searchTerm: leadSearch.trim() });
                setLeadResults(Array.isArray(res?.data) ? res.data : []);
            } catch (err) {
                setLeadResults([]);
            } finally {
                setLeadSearching(false);
            }
        }, 400);
        return () => clearTimeout(timer);
    }, [open, leadSearch]);

    // Poll the call while it's in flight
    useEffect(() => {
        if (!activeCallId) return;
        const poll = async () => {
            try {
                const res = await getCampaignCallById(activeCallId);
                if (res?.data) {
                    setCall(res.data);
                    if (["completed", "failed", "cancelled"].includes(res.data.status)) {
                        clearInterval(pollTimer.current);
                        pollTimer.current = null;
                    }
                }
            } catch (err) {
                // keep polling; transient errors are fine
            }
        };
        poll();
        pollTimer.current = setInterval(poll, 3000);
        return () => {
            if (pollTimer.current) clearInterval(pollTimer.current);
            pollTimer.current = null;
        };
    }, [activeCallId]);

    const resetAll = () => {
        setAgentId("");
        setFromNumber("");
        setLeadSearch("");
        setLeadResults([]);
        setSelectedLead(null);
        setActiveCallId(null);
        setCall(null);
        setRetellError("");
    };

    const handleClose = () => {
        resetAll();
        onClose();
    };

    const handleStart = async () => {
        setStarting(true);
        try {
            const res = await startTestCall({ agentId, fromNumber, leadId: selectedLead._id });
            if (res?.data?.callId) {
                toast.success(`Calling ${res.data.toNumber}…`);
                setActiveCallId(res.data.callId);
            }
        } catch (err) {
            toast.error(err?.response?.data?.message || "Failed to start test call");
        } finally {
            setStarting(false);
        }
    };

    const agentOptions = agents.map((a) => ({
        label: `${a.agent_name || "Unnamed agent"} (${a.agent_id})`,
        value: a.agent_id
    }));
    const numberOptions = phoneNumbers.map((n) => ({
        label: `${n.phone_number_pretty || n.phone_number}${n.nickname ? ` — ${n.nickname}` : ""}`,
        value: n.phone_number
    }));

    const leadName = (lead) => `${lead.firstName || ""} ${lead.lastName || ""}`.trim() || "Unnamed";
    const inFlight = call && ["queued", "calling"].includes(call.status);
    const finished = call && ["completed", "failed", "cancelled"].includes(call.status);

    return (
        <Dialog open={open} onOpenChange={(v) => !v && handleClose()}>
            <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
                <DialogHeader>
                    <DialogTitle>Test Call</DialogTitle>
                    <DialogDescription>
                        Call one lead with a Retell agent and watch the result, transcript, and lead status change live.
                    </DialogDescription>
                </DialogHeader>

                {!activeCallId ? (
                    <div className="space-y-4">
                        {retellLoading && (
                            <div className="flex items-center gap-2 text-sm text-gray-500">
                                <Loader2 className="h-4 w-4 animate-spin" /> Loading Retell agents and numbers…
                            </div>
                        )}
                        {retellError && (
                            <div className="p-3 bg-red-50 border border-red-100 rounded-lg text-sm text-red-700">{retellError}</div>
                        )}

                        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                            <div>
                                <Label className="text-xs font-semibold text-gray-700">Retell Agent *</Label>
                                <Select
                                    options={agentOptions}
                                    value={agentId}
                                    onChange={setAgentId}
                                    placeholder="Select agent"
                                    searchable
                                    className="mt-1"
                                />
                            </div>
                            <div>
                                <Label className="text-xs font-semibold text-gray-700">From Number *</Label>
                                <Select
                                    options={numberOptions}
                                    value={fromNumber}
                                    onChange={setFromNumber}
                                    placeholder="Select number"
                                    searchable
                                    className="mt-1"
                                />
                            </div>
                        </div>

                        <div>
                            <Label className="text-xs font-semibold text-gray-700">Lead *</Label>
                            {selectedLead ? (
                                <div className="mt-1 flex items-center justify-between p-3 bg-indigo-50/60 border border-indigo-100 rounded-lg">
                                    <div>
                                        <p className="text-sm font-medium text-gray-900">{leadName(selectedLead)}</p>
                                        <p className="text-xs text-gray-500 font-mono">{selectedLead.phone || "no phone"} • status: {selectedLead.status || "—"}</p>
                                    </div>
                                    <Button variant="outline" size="sm" onClick={() => setSelectedLead(null)}>Change</Button>
                                </div>
                            ) : (
                                <>
                                    <div className="relative mt-1">
                                        <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
                                        <Input
                                            value={leadSearch}
                                            onChange={(e) => setLeadSearch(e.target.value)}
                                            placeholder="Search by name, phone, or email…"
                                            className="pl-10"
                                        />
                                    </div>
                                    {leadSearching && <p className="text-xs text-gray-400 mt-1">Searching…</p>}
                                    {leadResults.length > 0 && (
                                        <div className="mt-1 border border-gray-200 rounded-lg divide-y divide-gray-100 max-h-52 overflow-y-auto">
                                            {leadResults.map((lead) => (
                                                <button
                                                    key={lead._id}
                                                    type="button"
                                                    onClick={() => { setSelectedLead(lead); setLeadResults([]); setLeadSearch(""); }}
                                                    className="w-full flex items-center justify-between px-3 py-2 text-left hover:bg-gray-50"
                                                    disabled={!lead.phone}
                                                >
                                                    <div>
                                                        <p className="text-sm text-gray-900">{leadName(lead)}</p>
                                                        <p className="text-xs text-gray-500 font-mono">{lead.phone || "no phone"} • {lead.status || "—"}</p>
                                                    </div>
                                                    {!lead.phone && <span className="text-[10px] text-red-400">no phone</span>}
                                                </button>
                                            ))}
                                        </div>
                                    )}
                                </>
                            )}
                        </div>

                        <div className="flex justify-end gap-2 pt-3 border-t border-gray-100">
                            <Button variant="outline" onClick={handleClose}>Cancel</Button>
                            <Button
                                onClick={handleStart}
                                disabled={!agentId || !fromNumber || !selectedLead || starting}
                                className="bg-indigo-600 hover:bg-indigo-700 text-white"
                            >
                                {starting ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <PhoneCall className="h-4 w-4 mr-2" />}
                                Start Test Call
                            </Button>
                        </div>
                    </div>
                ) : (
                    <div className="space-y-4">
                        {/* Live status */}
                        <div className="p-4 bg-gray-50 rounded-lg">
                            <div className="flex items-center justify-between">
                                <div className="flex items-center gap-2">
                                    {inFlight && <Loader2 className="h-4 w-4 animate-spin text-green-600" />}
                                    {finished && call.status === "completed" && <CheckCircle2 className="h-4 w-4 text-blue-600" />}
                                    {finished && call.status !== "completed" && <AlertTriangle className="h-4 w-4 text-red-500" />}
                                    <p className="text-sm font-medium text-gray-900">
                                        {call?.lead ? leadName(call.lead) : "…"} — <span className="font-mono text-xs">{call?.toNumber}</span>
                                    </p>
                                </div>
                                <Badge className={`text-xs ${CALL_STATUS_STYLES[call?.status] || ""}`}>{call?.status || "starting…"}</Badge>
                            </div>
                            {inFlight && (
                                <p className="text-xs text-gray-500 mt-2">
                                    Call in progress — this panel refreshes every 3 seconds. Transcript and analysis appear after the call ends.
                                </p>
                            )}
                            {call?.error && <p className="text-xs text-red-600 mt-2">{call.error}</p>}
                        </div>

                        {/* Result details */}
                        {finished && (
                            <div className="space-y-3">
                                <div className="grid grid-cols-2 gap-3">
                                    <div className="p-3 bg-gray-50 rounded-lg">
                                        <p className="text-[11px] font-semibold text-gray-500">Disconnection Reason</p>
                                        <p className="text-sm text-gray-900 font-mono">{call.disconnectionReason || "—"}</p>
                                    </div>
                                    <div className="p-3 bg-gray-50 rounded-lg">
                                        <p className="text-[11px] font-semibold text-gray-500">Call Successful</p>
                                        <p className="text-sm text-gray-900">{call.callSuccessful === undefined || call.callSuccessful === null ? "—" : String(call.callSuccessful)}</p>
                                    </div>
                                    <div className="p-3 bg-gray-50 rounded-lg">
                                        <p className="text-[11px] font-semibold text-gray-500">Voicemail</p>
                                        <p className="text-sm text-gray-900">{call.inVoicemail === undefined || call.inVoicemail === null ? "—" : String(call.inVoicemail)}</p>
                                    </div>
                                    <div className="p-3 bg-gray-50 rounded-lg">
                                        <p className="text-[11px] font-semibold text-gray-500">User Sentiment</p>
                                        <p className="text-sm text-gray-900">{call.userSentiment || "—"}</p>
                                    </div>
                                    <div className="p-3 bg-gray-50 rounded-lg col-span-2">
                                        <p className="text-[11px] font-semibold text-gray-500">Lead Status</p>
                                        <div className="flex items-center gap-2 text-sm text-gray-900">
                                            <span>{call.leadStatusBefore || "—"}</span>
                                            <ArrowRight className="h-3.5 w-3.5 text-gray-400" />
                                            <span className="font-semibold">{call.leadStatusAfter || call.leadStatusBefore || "—"}</span>
                                            {call.leadStatusAfter && call.leadStatusAfter !== call.leadStatusBefore ? (
                                                <Badge className="bg-green-100 text-green-700 text-[10px]">changed by mapping</Badge>
                                            ) : (
                                                <Badge className="bg-gray-100 text-gray-500 text-[10px]">no rule matched</Badge>
                                            )}
                                        </div>
                                    </div>
                                </div>

                                {call.recordingUrl && (
                                    <div>
                                        <p className="text-xs font-semibold text-gray-700 mb-1">Recording</p>
                                        <audio controls className="w-full" src={call.recordingUrl} />
                                    </div>
                                )}
                                {call.callSummary && (
                                    <div>
                                        <p className="text-xs font-semibold text-gray-700 mb-1">Summary</p>
                                        <p className="text-sm text-gray-700 bg-gray-50 rounded-lg p-3">{call.callSummary}</p>
                                    </div>
                                )}
                                {call.transcript && (
                                    <div>
                                        <p className="text-xs font-semibold text-gray-700 mb-1">Transcript</p>
                                        <pre className="text-xs text-gray-700 bg-gray-50 rounded-lg p-3 whitespace-pre-wrap font-sans max-h-64 overflow-y-auto">{call.transcript}</pre>
                                    </div>
                                )}
                                {Object.keys(call.customAnalysisData || {}).length > 0 && (
                                    <div>
                                        <p className="text-xs font-semibold text-gray-700 mb-1">Analysis Data</p>
                                        <pre className="text-xs text-gray-700 bg-gray-50 rounded-lg p-3 whitespace-pre-wrap max-h-40 overflow-y-auto">{JSON.stringify(call.customAnalysisData, null, 2)}</pre>
                                    </div>
                                )}
                            </div>
                        )}

                        <div className="flex justify-end gap-2 pt-3 border-t border-gray-100">
                            {finished && (
                                <Button variant="outline" onClick={() => { setActiveCallId(null); setCall(null); }}>
                                    New Test Call
                                </Button>
                            )}
                            <Button onClick={handleClose} className="bg-indigo-600 hover:bg-indigo-700 text-white">
                                {inFlight ? "Close (call continues)" : "Done"}
                            </Button>
                        </div>
                    </div>
                )}
            </DialogContent>
        </Dialog>
    );
}
